import { forwardRef, type CSSProperties, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Skeleton>. Sizing logic
// and CSS (pulse/shimmer animations, prefers-reduced-motion) ported
// letter-for-letter from the vendor's compiled bundle and its Skeleton CSS
// — new .rc-skeleton class names. The vendor also had a
// `:root[data-ds-mode="hc"]` (high-contrast) override; this app's own
// theme provider (src/lib/theme.tsx) never puts the app in "hc" mode, so
// that branch is unreachable here and isn't ported (same call made for
// AppSidebar's theme narrowing, see src/components/Sidebar.tsx).
export type SkeletonVariant = "text" | "circle" | "rectangle";
export type SkeletonAnimation = "pulse" | "shimmer" | "none";

export interface SkeletonProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  radius?: string | number;
  animation?: SkeletonAnimation;
}

function toCssSize(v: string | number | undefined, fallback: string): string {
  if (v === undefined) return fallback;
  return typeof v === "number" ? `${v}px` : v;
}

export const Skeleton = forwardRef<HTMLSpanElement, SkeletonProps>(function Skeleton(
  { variant = "rectangle", width, height, radius, animation = "pulse", className, style, ...rest },
  ref
) {
  const defaultHeight = variant === "rectangle" ? "8px" : "1em";
  const defaultWidth = variant === "circle" ? "1em" : "100%";

  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={cn("rc-skeleton", `rc-skeleton--${variant}`, animation !== "pulse" && `rc-skeleton--${animation}`, className)}
      style={{
        width: toCssSize(width, defaultWidth),
        height: toCssSize(height, defaultHeight),
        borderRadius: radius !== undefined ? toCssSize(radius, "") : undefined,
        ...style,
      } as CSSProperties}
      {...rest}
    />
  );
});
